import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; // Import the database configuration
import { collection, addDoc, getDocs, serverTimestamp, query, orderBy } from 'firebase/firestore';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch reviews from Firebase
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const q = query(collection(db, "reviews"), orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);
        const reviewsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter out any reviews the admin has explicitly hidden
        const visibleReviews = reviewsData.filter(review => review.isVisible !== false);
        
        setReviews(visibleReviews);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching reviews: ", error);
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Slideshow Logic
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === reviews.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? reviews.length - 1 : prevIndex - 1
    );
  };

  // Auto-slide effect
  useEffect(() => {
    // Do nothing if there are 1 or 0 reviews
    if (reviews.length <= 1) return;

    // Set interval to automatically change slide every 30 seconds (30000ms)
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === reviews.length - 1 ? 0 : prevIndex + 1
      );
    }, 30000); 

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [reviews.length]);

  // Submit Logic
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;

    setIsSubmitting(true);

    try {
      await addDoc(collection(db, "reviews"), {
        name: name,
        message: message,
        createdAt: serverTimestamp(),
        isVisible: true // Automatically set new reviews to be visible by default
      });

      // Clear form 
      setName('');
      setMessage('');
      alert("Review sent! Thank you.");
      
      // Refresh list from database to ensure accuracy
      const q = query(collection(db, "reviews"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const newReviewsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const visibleReviews = newReviewsData.filter(review => review.isVisible !== false);
      
      setReviews(visibleReviews);
      setCurrentIndex(0); // Go to newest
    } catch (error) {
      console.error("Error adding review: ", error);
      alert("Failed to send review.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="reviews-section" style={{ marginTop: 28 }}>
      <h2>Client Reviews</h2>
      
      {/* Slideshow Area */}
      <div className="card center-card review-container">
        {loading ? (
          <p>Loading reviews...</p>
        ) : reviews.length > 0 ? (
          <div className="review-slide">
            <div className="review-content">
              <p className="review-text">"{reviews[currentIndex]?.message}"</p>
              <h4 className="review-author">- {reviews[currentIndex]?.name}</h4>
            </div>
            
            <div className="review-controls">
              <button onClick={prevSlide} className="btn secondary small-btn">← Prev</button>
              <span className="review-indicators">
                {currentIndex + 1} / {reviews.length}
              </span>
              <button onClick={nextSlide} className="btn secondary small-btn">Next →</button>
            </div>
          </div>
        ) : (
          <p>No reviews yet. Be the first to write one!</p>
        )}
      </div>

      {/* Write a Review Form */}
      <div className="card dark-red-card review-form-card" style={{ marginTop: '20px' }}>
        <h3 className="card-title">Leave a Review</h3>
        <form onSubmit={handleSubmit} className="review-form">
          <input 
            type="text" 
            placeholder="Your Name" 
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-input"
            required
          />
          <textarea 
            placeholder="Your Experience with us..." 
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="form-input"
            rows="3"
            required
          />
          <center>
          <button type="submit" className="btn" disabled={isSubmitting} style={{marginTop: '10px', background: 'white', color: 'var(--primary-red)'}}>
            {isSubmitting ? "Sending..." : "Send Review"}
          </button>
          </center>
        </form>
      </div>
    </section>
  );
};

export default Reviews;